import React, { useState } from 'react';
import { useListarUsuarios, useCadastrarUsuario, useAlternarStatusUsuario, useListarEstabelecimentos } from '../../../hooks/useAdmin';
import { useAuth } from '../../../contexts/AuthContext';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { TipoUsuario } from '../../../types';
import Button from '../../../components/Common/Button';

interface FormData {
  nome: string;
  email: string;
  senha: string;
  tipo: TipoUsuario;
  estabelecimentoId: string;
}

const tipoLabels: Record<TipoUsuario, string> = {
  [TipoUsuario.Administrador]: 'Administrador',
  [TipoUsuario.GerenteEstabelecimento]: 'Gerente de Estabelecimento',
  [TipoUsuario.FuncionarioEstabelecimento]: 'Funcionário',
};

const tiposParaCadastro = [TipoUsuario.Administrador, TipoUsuario.GerenteEstabelecimento];

const AdminUsuarios: React.FC = () => {
  usePageTitle('Admin - Usuários');
  const { user: usuarioLogado } = useAuth();
  const { data: usuarios, isLoading } = useListarUsuarios();
  const { data: estabelecimentos } = useListarEstabelecimentos();
  const { mutate: cadastrar, isPending: cadastrando } = useCadastrarUsuario();
  const { mutate: alternarStatus } = useAlternarStatusUsuario();

  const [mostrarModal, setMostrarModal] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    nome: '',
    email: '',
    senha: '',
    tipo: TipoUsuario.GerenteEstabelecimento,
    estabelecimentoId: '',
  });

  const precisaEstabelecimento = formData.tipo !== TipoUsuario.Administrador;

  const abrirModal = () => {
    setFormData({ nome: '', email: '', senha: '', tipo: TipoUsuario.GerenteEstabelecimento, estabelecimentoId: '' });
    setMostrarModal(true);
  };

  const fecharModal = () => setMostrarModal(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!usuarioLogado) return;

    cadastrar(
      {
        nome: formData.nome,
        email: formData.email,
        senha: formData.senha,
        tipo: formData.tipo,
        estabelecimentoId: precisaEstabelecimento && formData.estabelecimentoId ? parseInt(formData.estabelecimentoId) : null,
        usuarioLogadoId: usuarioLogado.id,
      },
      { onSuccess: fecharModal }
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Usuários</h1>
          <p className="text-gray-500 text-sm mt-1">Gerencie os usuários do sistema</p>
        </div>
        <Button onClick={abrirModal} variant="primary" size="md">
          Novo Usuário
        </Button>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center py-8">Carregando usuários...</p>
        </div>
      ) : usuarios && usuarios.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estabelecimento</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {usuarios.map((u) => {
                const nomeEstabelecimento = u.estabelecimentoId
                  ? estabelecimentos?.find((e) => e.id === u.estabelecimentoId)?.nome ?? `#${u.estabelecimentoId}`
                  : '-';

                return (
                  <tr key={u.id} className={!u.ativo ? 'bg-gray-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{u.nome}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{u.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tipoLabels[u.tipo]}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{nomeEstabelecimento}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        u.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {u.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      {u.id !== usuarioLogado?.id && (
                        <button
                          onClick={() => alternarStatus(u.id)}
                          className={u.ativo ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}
                        >
                          {u.ativo ? 'Desativar' : 'Ativar'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center py-8">Nenhum usuário encontrado</p>
        </div>
      )}

      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Novo Usuário</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
                <input
                  type="password"
                  value={formData.senha}
                  onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Mínimo 6 caracteres"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value as TipoUsuario, estabelecimentoId: '' })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  {tiposParaCadastro.map((tipo) => (
                    <option key={tipo} value={tipo}>{tipoLabels[tipo]}</option>
                  ))}
                </select>
              </div>

              {precisaEstabelecimento && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estabelecimento</label>
                  <select
                    value={formData.estabelecimentoId}
                    onChange={(e) => setFormData({ ...formData, estabelecimentoId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    required
                  >
                    <option value="">Selecione...</option>
                    {estabelecimentos?.map((est) => (
                      <option key={est.id} value={est.id}>{est.nome}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button type="submit" variant="primary" size="md" fullWidth disabled={cadastrando}>
                  {cadastrando ? 'Salvando...' : 'Cadastrar'}
                </Button>
                <Button type="button" onClick={fecharModal} variant="secondary" size="md" fullWidth disabled={cadastrando}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsuarios;
